import { ProductVariantAvailabilityStatus } from "@swo/shared";
import { PdpData } from "../types";
import { ShoppingCartSvgIcon } from "../../icons";
import { interpolateLinearly } from "../../../../domain/translations/simple-translations-module";
import { PdpAvailabilityState, PDPButtonWithAction, pdpDisabledActionOf } from "./types";
import { FormatDateF } from "./pdp-availability";
import type { TranslatedValue } from "@swo/storefront";
import { mapTranslatedValue } from "../../../../components/T";

export function getVariantCartState(
  availability: ProductVariantAvailabilityStatus,
  formatDate: FormatDateF,
  addToCartAction: () => Promise<void>,
  translations: PdpData["translations"]["pdp"],
  activeDealerLocatorButton: PDPButtonWithAction,
  availabilityNotification: (text: TranslatedValue) => PdpAvailabilityState,
): PdpAvailabilityState {
  const cartLabel = translations.cartLabel;

  const inStockFromText = (inStockFrom: string | undefined) => {
    if (!inStockFrom) {
      return translations.outOfStockLabel;
    }

    const inStockFromDate = new Date(inStockFrom);
    const inStockFromString = formatDate(inStockFromDate);

    return mapTranslatedValue(translations.outOfStockWithDateLabel, _ => interpolateLinearly(_, [inStockFromString]));
  };

  switch (availability.tag) {
    case "in-stock": {
      return {
        action: { tag: "action", f: addToCartAction, label: cartLabel, icon: ShoppingCartSvgIcon },
        extension: null,
      };
    }

    case "at-dealers-only": {
      return {
        action: activeDealerLocatorButton,
        extension: { tag: "text", text: translations.onlyWithDealersLabel },
      };
    }

    case "discontinued": {
      return {
        action: pdpDisabledActionOf(cartLabel, ShoppingCartSvgIcon),
        extension: { tag: "text", text: translations.discontinuedLabel },
      };
    }

    case "not-yet-available-for-sale": {
      const availableFrom = formatDate(new Date(availability.availableFrom));

      const v = availability.inStockFrom
        ? inStockFromText(availability.inStockFrom)
        : mapTranslatedValue(translations.presaleLabel, _ => interpolateLinearly(_, [availableFrom]));

      return availabilityNotification(v);
    }

    case "preorder": {
      const availableFrom = formatDate(new Date(availability.availableFrom), {
        day: "numeric",
        month: "long",
        year: "numeric",
      });

      return {
        action: { tag: "action", f: addToCartAction, label: cartLabel, icon: ShoppingCartSvgIcon },
        extension: {
          tag: "text",
          text: mapTranslatedValue(translations.preorderLabel, _ => interpolateLinearly(_, [availableFrom])),
        },
      };
    }

    case "out-of-stock": {
      return availabilityNotification(inStockFromText(availability.inStockFrom));
    }

    default: {
      const _: never = availability;

      return _;
    }
  }
}
